import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { registerStaff } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Sprout, AlertCircle, CheckCircle, Loader2, UserPlus } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-2xl flex items-center justify-center shadow-lg">
                <Sprout className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-[#2D4A4A]">Growing Minds Tuition</h1>
            <p className="text-[#5A7A7A] mt-1">Staff Training Platform</p>
          </Link>
        </div>

        <Card className="border border-[#A8E6CF]/30 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl text-[#2D4A4A]">Sign In</CardTitle>
            <CardDescription className="text-[#5A7A7A]">Enter your credentials to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2D4A4A]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#2D4A4A]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                  data-testid="login-password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-4 text-center space-y-2">
              <Link 
                to="/forgot-password" 
                className="text-sm text-[#5BA4A4] hover:text-[#4A9090] hover:underline block"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
              <p className="text-sm text-[#5A7A7A]">
                New staff member?{' '}
                <Link 
                  to="/register" 
                  className="text-[#5BA4A4] hover:text-[#4A9090] hover:underline font-medium"
                  data-testid="register-link"
                >
                  Register here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#5A7A7A] hover:text-[#5BA4A4]">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await forgotPassword(email);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-2xl flex items-center justify-center shadow-lg">
                <Sprout className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-[#2D4A4A]">Growing Minds Tuition</h1>
          </Link>
        </div>

        <Card className="border border-[#A8E6CF]/30 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl text-[#2D4A4A]">Reset Password</CardTitle>
            <CardDescription className="text-[#5A7A7A]">Enter your email to receive a password reset link</CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <Alert className="border-[#A8E6CF] bg-[#F0FAF7]">
                <CheckCircle className="h-4 w-4 text-[#5BA4A4]" />
                <AlertDescription className="text-[#2D4A4A]">
                  If an account exists with that email, you will receive a password reset link.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <AlertDescription className="text-red-700">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2D4A4A]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            )}

            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-[#5BA4A4] hover:text-[#4A9090] hover:underline">
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await resetPassword(token, password);
    
    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5] p-4">
        <Card className="w-full max-w-md border border-[#A8E6CF]/30 shadow-lg">
          <CardContent className="pt-6">
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <AlertDescription className="text-red-700">Invalid or missing reset token</AlertDescription>
            </Alert>
            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-[#5BA4A4] hover:underline">
                Request a new reset link
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-2xl flex items-center justify-center shadow-lg">
              <Sprout className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[#2D4A4A]">Growing Minds Tuition</h1>
        </div>

        <Card className="border border-[#A8E6CF]/30 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl text-[#2D4A4A]">Set New Password</CardTitle>
            <CardDescription className="text-[#5A7A7A]">Enter your new password below</CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <Alert className="border-[#A8E6CF] bg-[#F0FAF7]">
                <CheckCircle className="h-4 w-4 text-[#5BA4A4]" />
                <AlertDescription className="text-[#2D4A4A]">
                  Password reset successfully! Redirecting to login...
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <AlertDescription className="text-red-700">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2D4A4A]">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="reset-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#2D4A4A]">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="reset-confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                  disabled={loading}
                  data-testid="reset-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await registerStaff({ name, email, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-2xl flex items-center justify-center shadow-lg">
                <Sprout className="w-8 h-8 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-[#2D4A4A]">Growing Minds Tuition</h1>
            <p className="text-[#5A7A7A] mt-1">Staff Registration</p>
          </Link>
        </div>

        <Card className="border border-[#A8E6CF]/30 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-xl text-[#2D4A4A] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#5BA4A4]" />
              Register
            </CardTitle>
            <CardDescription className="text-[#5A7A7A]">
              Create your account. An admin will review and approve your registration.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <Alert className="border-[#A8E6CF] bg-[#F0FAF7]">
                <CheckCircle className="h-4 w-4 text-[#5BA4A4]" />
                <AlertDescription className="text-[#2D4A4A]">
                  Registration submitted successfully! An administrator will review your application and you'll receive an email once approved.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <AlertDescription className="text-red-700">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="reg-name" className="text-[#2D4A4A]">Full Name</Label>
                  <Input
                    id="reg-name"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-[#2D4A4A]">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-[#2D4A4A]">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="register-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-confirm" className="text-[#2D4A4A]">Confirm Password</Label>
                  <Input
                    id="reg-confirm"
                    type="password"
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] focus:ring-[#5BA4A4]"
                    data-testid="register-confirm-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Register'
                  )}
                </Button>
              </form>
            )}

            <div className="mt-4 text-center">
              <p className="text-sm text-[#5A7A7A]">
                Already have an account?{' '}
                <Link to="/login" className="text-[#5BA4A4] hover:text-[#4A9090] hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-[#5A7A7A] hover:text-[#5BA4A4]">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};
